import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Paper } from '@mui/material';
import FilterBar from './FilterBar';
import { AlertBar, Loader } from '~/shared/components';
import { useContainerReady, useDebouncedResizeObserver } from '~/shared/hooks';
import { downloadHeatmapData } from '~/shared/services/api';
import { useHeatmapData, useNavAndFilters, useScroller } from '~/shared/services/charts';
import { tooltipCfg } from '~/shared/config/tooltipCfg';
import HeadlessTableChart from './HeadlessTableChart/HeadlessTableChart';

const ScrollWrap = styled.div`
  flex: 1;
  overflow-y: scroll;
  height: 100%;
  max-width: 100%;
  ${({ theme }: any) => `
    padding: ${theme.spacing(3)}px;
  `};
`;

const MainContainer = styled(Paper)`
  max-width: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px;
`;

const ChartRow = styled.div`
  display: flex;
  width: 100%;
`;

const HeatmapContainer = styled.div`
  flex: 1;
  position: relative;
  padding: 8px;
  width: 100%;
`;

const FilterBarRow = styled.div`
  width: 100%;
  padding: 0 8px 8px;
`;

const RelativeContainer = styled.div<{ $slotheight: number }>`
  position: relative;
  height: 100%;
  width: 100%;
  min-height: ${({ $slotheight }) => $slotheight}px;
`;

const LoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-chart, #ffffff);
  z-index: 2;
`;

type HeadlessTableChartPageProps = {
  navHirarchy: any;
  gridIndex?: number;
  gridHeight?: number;
  gridFilterValues?: any;
  gridFilterValuesID?: any;
};

export default function HeadlessTableChartPage({
  navHirarchy,
  gridIndex,
  gridHeight,
  gridFilterValues,
  gridFilterValuesID,
}: HeadlessTableChartPageProps) {
  const { scrollerRef } = useScroller();
  const { category, subcategory, subsubcategory, prism, navHirarchyItem, filterValues, filterValuesID, prismId }: any =
    useNavAndFilters(navHirarchy, gridIndex);

  const effectiveFilterValues = useMemo(
    () => (gridFilterValues != null ? gridFilterValues : filterValues),
    [gridFilterValues, filterValues],
  );

  const effectiveFilterValuesID = useMemo(
    () => (gridFilterValuesID != null ? gridFilterValuesID : filterValuesID),
    [gridFilterValuesID, filterValuesID],
  );

  const { data, error, isLoading, chartTitle, chartSubtitle } = useHeatmapData(
    category,
    subcategory,
    subsubcategory,
    prism,
    effectiveFilterValuesID,
    prismId,
  );

  const resolvedChartTitle = useMemo(() => {
    if (chartTitle && chartTitle.trim().length > 0) {
      return chartTitle.trim();
    }
    const fallback = navHirarchyItem?.heatmap?.title ?? navHirarchyItem?.heatmap?.name ?? navHirarchyItem?.name ?? '';
    return (fallback || '').trim();
  }, [chartTitle, navHirarchyItem]);

  const { size, ref: chartSizeRef }: any = useDebouncedResizeObserver(100);
  const isContainerReady = useContainerReady(size, { minWidth: 40, minHeight: 0 });
  const safeGridHeight =
    typeof gridHeight === 'number' && Number.isFinite(gridHeight) && gridHeight > 0 ? gridHeight : 4;
  const slotHeightPx = Math.max((safeGridHeight / 4) * 454 + (safeGridHeight / 4 - 1) * 40, 160);

  const onDownload = () =>
    downloadHeatmapData(
      category,
      subcategory,
      subsubcategory,
      prism,
      effectiveFilterValuesID,
      prismId,
      resolvedChartTitle,
    ).catch(e => {
      alert(e.message);
    });

  return (
    <ScrollWrap ref={scrollerRef}>
      {error ? <AlertBar message={(error as unknown as any).message} /> : <div />}
      <MainContainer id="headlessTableChart">
        <ContentContainer>
          {!gridIndex && gridIndex !== 0 && (
            <FilterBarRow>
              <FilterBar
                type={navHirarchyItem?.type}
                filters={navHirarchyItem?.filters}
                values={effectiveFilterValues}
                filterHierarchy={navHirarchyItem?.narrativeFilterTree}
                filterHierarchyValues={effectiveFilterValuesID}
                onDownload={onDownload}
                tooltipCfg={tooltipCfg}
              />
            </FilterBarRow>
          )}
          <ChartRow>
            <HeatmapContainer ref={chartSizeRef}>
              <RelativeContainer $slotheight={slotHeightPx}>
                {(!isContainerReady || isLoading) && (
                  <LoaderOverlay>
                    <Loader />
                  </LoaderOverlay>
                )}
                {isContainerReady && !isLoading && data && (
                  <HeadlessTableChart
                    key={resolvedChartTitle || 'headless-table-chart'}
                    data={data}
                    chartTitle={resolvedChartTitle}
                    chartSubtitle={chartSubtitle}
                    gridHeight={gridHeight}
                  />
                )}
              </RelativeContainer>
            </HeatmapContainer>
          </ChartRow>
        </ContentContainer>
      </MainContainer>
    </ScrollWrap>
  );
}
